import { useMemo } from 'react'
import { useQuery } from 'urql'
import { CONTACT_THREAD_IDS_QUERY, SMS_AGENT_READY_QUERY, THREAD_AGENT_STATE_QUERY } from 'app/graphql/agentThreads'
import { OPS_URL } from 'app/lib/opsUrqlClient'

/**
 * Whether the AI agent is replying on this contact's thread, by the same
 * definition the inbox uses for its "AI Agent is replying" banner: the location
 * has a live agent AND the thread has it switched on.
 *
 * Both halves are load-bearing. `aiEnabled` is true on most threads by default,
 * so gating on it alone would withhold scheduling at every location that has no
 * agent configured at all.
 *
 * `aiEnabled === true` is strict on purpose. An absent or null value means the
 * thread has no agent state, not that the agent is on, and a gate that guessed
 * "on" while loading would make the Schedule control flicker in and out.
 */
export function useThreadAgentHandling(
  locationId: string | null | undefined,
  contactId: string | null | undefined,
  enabled = true,
): boolean {
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])

  // `enabled` carries the scheduled-sms toggle. The only thing this answers is
  // whether the Schedule control may be offered, so with the feature off every
  // read here would cost each surface three requests to decide nothing.
  const [{ data: readyData }] = useQuery({
    query: SMS_AGENT_READY_QUERY,
    variables: { locationId: locationId! },
    pause: !locationId || !enabled,
    context: opsContext,
  })
  const agentReady = readyData?.smsAgentReady === true

  // Both remaining reads wait on a location that has an agent at all, which is
  // the common case and costs nothing: with no agent the thread's own flag
  // cannot make this true.
  const [{ data: messageData }] = useQuery({
    query: CONTACT_THREAD_IDS_QUERY,
    variables: {
      search: `contactId:${contactId} AND threadId:*`,
      pagination: { limit: 1 },
      sort: { field: `createdDate`, direction: `DESC` },
    },
    pause: !contactId || !agentReady,
  })
  const threadId = messageData?.messages?.hits?.[0]?.threadId

  const [{ data: threadData }] = useQuery({
    query: THREAD_AGENT_STATE_QUERY,
    variables: { id: threadId! },
    pause: !threadId,
  })

  return agentReady && threadData?.thread?.aiEnabled === true
}
